import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Icon } from "@/components/Icon";
import PageContainer from "@/components/ui/PageContainer";
import LoadError from "@/components/ui/LoadError";
import EmptyState from "@/components/ui/EmptyState";

const STATUS_STYLE = {
  open: "bg-surface-5 text-pnr-green",
  answered: "bg-pnr-green/15 text-pnr-green",
  closed: "bg-surface-5 text-meta",
};

export default function Support() {
  const [tickets, setTickets] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  // Distinguish a real load failure (show a retry) from a genuinely-empty ticket list (UIA-02).
  const load = () => { setLoadErr(false); return api.get("/support/me").then(({ data }) => setTickets(data)).catch(() => { setLoadErr(true); setTickets(null); }); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) { toast.error("Fill in subject and message"); return; }
    setBusy(true);
    try {
      await api.post("/support", { subject, message });
      toast.success("Sent! The admin will get back to you here.");
      // Clear the draft only after a confirmed send — a failed send preserves what you typed.
      setSubject(""); setMessage("");
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <PageContainer clearBottomNav data-testid="support-page">
      <div className="pt-1">
        <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-pnr-green">Need a hand?</p>
        <h1 className="mt-1 flex items-center gap-2.5 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">
          <Icon name="support" size={28} className="text-pnr-green" /> PRYMEBET Support
        </h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="surface-premium rounded-2xl p-5 sm:p-6 lg:col-span-2"
        >
          <h2 className="mb-4 font-display text-lg font-black tracking-tight text-white sm:text-xl">Ask a question</h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input id="subject" data-testid="support-subject-input" value={subject}
                autoComplete="off" enterKeyHint="next"
                onChange={(e) => setSubject(e.target.value)} placeholder="Deposit not showing up" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="msg">Message</Label>
              <Textarea id="msg" data-testid="support-message-input" value={message} rows={6}
                onChange={(e) => setMessage(e.target.value)} placeholder="Describe your issue…" className="mt-1.5 resize-y leading-relaxed" />
            </div>
            <Button type="submit" data-testid="support-submit-button" disabled={busy}
              className="h-12 w-full rounded-full bg-pnr-green font-bold text-surface-1 transition-colors hover:bg-pnr-green-hover focus-ring disabled:cursor-not-allowed disabled:bg-pnr-green/40 disabled:text-surface-1/60">
              {busy ? "Sending…" : "Send message"}
            </Button>
          </div>
        </motion.form>

        <div className="lg:col-span-3">
          <h2 className="mb-3 font-display text-lg font-black tracking-tight text-white sm:text-xl">Your tickets</h2>
          {loadErr ? (
            <LoadError onRetry={load} message="We couldn't load your tickets." testid="support-load-error" />
          ) : tickets === null ? (
            <div className="flex justify-center py-12"><Icon name="refresh" size={24} className="animate-spin text-pnr-green" /></div>
          ) : tickets.length === 0 ? (
            <EmptyState icon="support" message="No tickets yet. Use the form to send your first message." testid="support-empty" />
          ) : (
            <div className="space-y-3">
              {tickets.map((t) => (
                <div key={t.id} data-testid={`ticket-${t.id}`} className="surface-premium rounded-2xl p-4">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <p className="font-semibold text-white">{t.subject}</p>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLE[t.status] || "bg-surface-5 text-meta"}`}>{t.status}</span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-meta">{t.message}</p>
                  {t.reply && (
                    <div className="mt-3 flex gap-2 rounded-xl bg-pnr-green/10 p-3">
                      <Icon name="mail" size={16} className="mt-0.5 shrink-0 text-pnr-green" />
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-pnr-green">Admin reply</p>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed text-white">{t.reply}</p>
                      </div>
                    </div>
                  )}
                  <p className="mt-2 text-xs tabular-nums text-[#71717a]">{new Date(t.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
